/**
 * WHICH GLASS PATH THIS ENGINE GETS, decided once and written onto <html> as
 * `data-glass-path` (`ui/glass.css` reads it, `ui/GlassFilters.tsx` supplies the filters).
 *
 * THE HARD PART IS NOT THE TEST, IT IS THAT THE OBVIOUS TEST LIES. `backdrop-filter:
 * url(#id)` PARSES in every engine that ships `backdrop-filter` at all - measured
 * 2026-09-10 in both engines on the same probe page, `CSS.supports('backdrop-filter',
 * 'url(#lens)')` came back `true` in Chromium AND in WebKit - while only Chromium PAINTS
 * it. WebKit's own bug 297770 says why: "Accelerated filters (including backdrop-filter)
 * can't use SVG filters, sadly" (Simon Fraser, 2025-08-25). A parse test on its own would
 * therefore hand WebKit a declaration it cannot execute, and the cost of being wrong is not
 * cosmetic: the lens sits in the SAME declaration as the blur (see glass.css for why it has
 * to), so an engine that drops the declaration drops the blur with it and chrome stops
 * being legible over content.
 *
 * A RENDER PROBE WOULD BE THE HONEST TEST AND THE PLATFORM DOES NOT OFFER ONE: nothing in
 * the DOM reports whether a backdrop filter painted, and no API reads back the pixels of a
 * live element. The research says the same - neither source carries a detection recipe.
 *
 * SO THE TEST IS A PAIR, AND BOTH HALVES WERE MEASURED IN BOTH ENGINES ON 2026-09-10:
 *   `CSS.supports('backdrop-filter', 'url(#...)')`          Chromium true   WebKit true
 *   `CSS.supports('-webkit-backdrop-filter', 'url(#...)')`  Chromium false  WebKit true
 * The second is not "which browser is this". It asks whether the engine still ships the
 * pre-standard property, and the engines that do are exactly the ones whose accelerated
 * filter pipeline refuses SVG filters - WebKit by its own bug report, Gecko by the same
 * finding in the research (LogRocket 2025-12-08: "Safari and Firefox do not support this
 * combination yet"). It is deliberately CONSERVATIVE in one direction: an engine this test
 * is unsure about gets the frosted material, which is what it has today and is never worse.
 * When WebKit fixes 297770 this test will still withhold refraction, and the fix is to
 * replace it with a render probe on the day the platform grows one.
 */
export type GlassPath = 'refraction' | 'frosted';

/** The value both halves of the test ask about. Any filter id would do; this one exists. */
const LENS = 'url("#glass-lens-pane")';

type Supports = (property: string, value: string) => boolean;

export function resolveGlassPath(supports?: Supports): GlassPath {
  const test =
    supports ??
    (typeof CSS !== 'undefined' && typeof CSS.supports === 'function'
      ? (property: string, value: string) => CSS.supports(property, value)
      : undefined);
  // No CSS.supports at all (a unit environment, a very old engine): frosted, like everyone
  // else who cannot answer the question.
  if (!test) return 'frosted';
  if (!test('backdrop-filter', LENS)) return 'frosted';
  if (test('-webkit-backdrop-filter', LENS)) return 'frosted';
  return 'refraction';
}

/**
 * Called once from `main.tsx`, before the first render, so no surface ever paints the
 * frosted path and then jumps to the refracted one.
 */
export function applyGlassPath(root?: HTMLElement, supports?: Supports): GlassPath {
  const path = resolveGlassPath(supports);
  const element = root ?? (typeof document !== 'undefined' ? document.documentElement : undefined);
  if (element) element.dataset['glassPath'] = path;
  return path;
}
